/**
 * Cliente API + hooks de citas IA persistidas (ADR-0001 Tier 1) y de su
 * respaldo (OT-0049).
 *
 *   - `applySectionDraftRunCitations` → POST /artifacts/{aid}/section-draft-runs/{run_id}/citations
 *   - `getVersionCitations`           → GET  /artifacts/{aid}/versions/{vid}/citations
 *   - `getVersionGroundings`          → GET  /artifacts/{aid}/versions/{vid}/groundings
 *   - `reviewAiObservation`           → POST   …/groundings/{gid}/observations/{i}/review
 *   - `undoAiObservationReview`       → DELETE …/groundings/{gid}/observations/{i}/review
 *
 * Las citas y su evidencia las guarda el servidor desde la corrida de la
 * IA: el cliente sólo dice dónde quedó el texto (`instance_key`), nunca
 * manda las citas.
 *
 * Mismo patrón de firma que el resto de los clientes (accessToken
 * primer argumento). Los hooks React Query encapsulan las query keys
 * `['version-citations', versionId]` y `['version-groundings', versionId]`.
 */

import { type QueryClient, useQuery, type UseQueryResult } from '@tanstack/react-query'

import { useAccessToken } from '../auth/useAccessToken'
import { apiFetch } from './client'

// ─── Tipos ────────────────────────────────────────────────────────────────
/** Motivo por el que una cita no quedó respaldada (OT-0049). */
export type CitationUnverifiedReason =
  | 'not_supporting'
  | 'miscited'
  | 'quote_not_found'
  | 'missing_evidence'
  | 'invalid_ref'

/** Una oración que usa una cita `[N]`, con la frase literal de la fuente
 * y si el juez confirmó que la frase la respalda. */
export interface CitationClaim {
  claim: string
  quote: string
  status: 'verified' | 'unverified'
  reason: CitationUnverifiedReason | null
}

/** Etiqueta de una oración que el revisor debe ver: no respaldada, mal
 * citada o que el juez no alcanzó a evaluar. */
export type GroundingFlagLabel = 'miscited' | 'unsupported' | 'unjudged'

export interface GroundingFinding {
  sentence: string
  label: GroundingFlagLabel
  why: string
}

/** Juicio por oración de un texto generado por IA: conteo por etiqueta y
 * oraciones marcadas. */
export interface GroundingReport {
  counts: Record<string, number>
  flagged: GroundingFinding[]
}

/** Quién revisó una observación, cuándo y con qué nota. */
export interface ObservationReview {
  reviewed_by_user_id: string | null
  reviewed_by_display_name: string | null
  reviewed_at: string
  note: string | null
}

/** Observación guardada de una sección: su posición y su revisión
 * (`null` mientras nadie la revise). */
export interface SectionObservation extends GroundingFinding {
  index: number
  review: ObservationReview | null
}

/** Verificación por oración guardada de una sección de una versión. Una
 * versión no se envía a revisión con observaciones sin revisar. */
export interface SectionGroundingRead extends GroundingReport {
  id: string
  artifact_version_id: string
  section_id: string
  instance_key: string
  flagged: SectionObservation[]
  created_at: string
}

/** Observaciones de la versión que nadie ha marcado como revisadas. */
export function pendingObservations(groundings: SectionGroundingRead[]): number {
  return groundings.reduce(
    (sum, g) => sum + g.flagged.filter((f) => f.review === null).length,
    0,
  )
}

/** Respuesta de POST y elementos del array de GET. */
export interface CitationRead {
  id: string
  artifact_version_id: string
  section_id: string
  instance_key: string
  ref_number: number
  chunk_id: string
  source_id: string | null
  scope: string
  chunk_index: number
  section_path: string[]
  page_number: number | null
  score: number
  rerank_score: number | null
  filename: string | null
  origin: string | null
  preview: string
  /** `null` = cita anterior al registro de evidencia (sin frase). */
  evidence: CitationClaim[] | null
  created_at: string
}

// ─── Cliente ──────────────────────────────────────────────────────────────
/** Guarda las citas (con su evidencia) y el juicio por oración de una
 * corrida de "Generar una sección". `instanceKey` es dónde quedó el texto;
 * `null` para una sección en root. Se aplica una sola vez. */
export async function applySectionDraftRunCitations(
  accessToken: string,
  artifactId: string,
  runId: string,
  instanceKey: string | null,
): Promise<CitationRead[]> {
  return apiFetch<CitationRead[]>(
    `/api/v1/artifacts/${artifactId}/section-draft-runs/${runId}/citations`,
    { method: 'POST', accessToken, body: { instance_key: instanceKey } },
  )
}

export async function getVersionGroundings(
  accessToken: string,
  artifactId: string,
  versionId: string,
): Promise<SectionGroundingRead[]> {
  return apiFetch<SectionGroundingRead[]>(
    `/api/v1/artifacts/${artifactId}/versions/${versionId}/groundings`,
    { method: 'GET', accessToken },
  )
}

/** Marca una observación como revisada: el autor corrigió la oración o la
 * confirmó como está. `sentence` es la que vio; si la sección se regeneró, el
 * servidor responde `AI_OBSERVATION_NOT_FOUND`. */
export async function reviewAiObservation(
  accessToken: string,
  args: {
    artifactId: string
    versionId: string
    groundingId: string
    index: number
    sentence: string
    note: string | null
  },
): Promise<SectionGroundingRead> {
  return apiFetch<SectionGroundingRead>(observationReviewPath(args), {
    method: 'POST',
    accessToken,
    body: { sentence: args.sentence, note: args.note },
  })
}

/** Deshace la revisión; la observación vuelve a quedar pendiente. */
export async function undoAiObservationReview(
  accessToken: string,
  args: { artifactId: string; versionId: string; groundingId: string; index: number },
): Promise<SectionGroundingRead> {
  return apiFetch<SectionGroundingRead>(observationReviewPath(args), {
    method: 'DELETE',
    accessToken,
  })
}

function observationReviewPath(args: {
  artifactId: string
  versionId: string
  groundingId: string
  index: number
}): string {
  return (
    `/api/v1/artifacts/${args.artifactId}/versions/${args.versionId}` +
    `/groundings/${args.groundingId}/observations/${args.index}/review`
  )
}

export async function getVersionCitations(
  accessToken: string,
  artifactId: string,
  versionId: string,
): Promise<CitationRead[]> {
  return apiFetch<CitationRead[]>(
    `/api/v1/artifacts/${artifactId}/versions/${versionId}/citations`,
    { method: 'GET', accessToken },
  )
}

// ─── Hooks ────────────────────────────────────────────────────────────────
/** React Query hook para fetch de citas de una versión. Lo consume el
 * viewer/editor al renderizar `[N]` para resolver el popover de fuente.
 * Lazy: solo dispara cuando `versionId` está populado. */
export function useVersionCitations(args: {
  artifactId: string | null
  versionId: string | null
  enabled?: boolean
}): UseQueryResult<CitationRead[], Error> {
  const { accessToken } = useAccessToken()
  const { artifactId, versionId, enabled = true } = args
  return useQuery({
    queryKey: ['version-citations', versionId],
    queryFn: async () => {
      if (!accessToken || !artifactId || !versionId) {
        return [] as CitationRead[]
      }
      return getVersionCitations(accessToken, artifactId, versionId)
    },
    enabled: enabled && !!accessToken && !!artifactId && !!versionId,
    staleTime: 30_000,
  })
}

/** Query key estable para invalidar después del POST. Lo usan
 * SectionEditor.handleAiAccept y el orchestrator-side dialog. */
export function versionCitationsQueryKey(versionId: string): unknown[] {
  return ['version-citations', versionId]
}

/** Juicio por oración de cada sección de una versión: las oraciones que el
 * revisor debe ver antes de aprobar. */
export function useVersionGroundings(args: {
  artifactId: string | null
  versionId: string | null
  enabled?: boolean
}): UseQueryResult<SectionGroundingRead[], Error> {
  const { accessToken } = useAccessToken()
  const { artifactId, versionId, enabled = true } = args
  return useQuery({
    queryKey: versionGroundingsQueryKey(versionId ?? ''),
    queryFn: async () => {
      if (!accessToken || !artifactId || !versionId) {
        return [] as SectionGroundingRead[]
      }
      return getVersionGroundings(accessToken, artifactId, versionId)
    },
    enabled: enabled && !!accessToken && !!artifactId && !!versionId,
    staleTime: 30_000,
  })
}

export function versionGroundingsQueryKey(versionId: string): unknown[] {
  return ['version-groundings', versionId]
}

/** Refresca las citas y las observaciones de una versión después de que la
 * IA escribió en ella ("Generar sección", "Generar todo", "Subsanar con
 * IA"): el servidor reemplazó ambas y la web no debe mostrar las anteriores
 * ni permitir enviar a revisión con un conteo viejo. */
export function invalidateVersionAiMetadata(
  queryClient: QueryClient,
  versionId: string,
): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: versionCitationsQueryKey(versionId) }),
    queryClient.invalidateQueries({ queryKey: versionGroundingsQueryKey(versionId) }),
  ]).then(() => undefined)
}
